/**
 * VPE Dashboard Operations Hook
 * Handles all business logic and operations for the VPEDashboard component
 * Extracted from VPEDashboard.tsx for better separation of concerns
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useGroups } from '@/hooks/ea/useGroups.ts';
import { vpeDashboardService, Group } from '@/services/ea/vpeDashboardService';
import { VPEDashboardState, VPEDashboardStateActions } from './useEADashboardState';
import { createGroupOperationsNavigation } from '@/utils/groupOperationsNavigation';

export interface VPEDashboardOperations {
  // Core Operations
  handleRefresh: () => Promise<void>;
  handleLogout: () => void;

  // Search Operations
  handleSearch: () => void;
  handleClearSearch: () => void;
  handlePatientSearch: () => Promise<void>;
  handleSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

  // Group Operations
  handleArchiveGroup: () => Promise<void>;
  handleDeleteGroup: () => Promise<void>;
  handleAddPatientToGroup: () => Promise<void>;

  // Navigation Operations
  handleEmailClick: (groupId: number) => void;
  handleArchiveClick: (groupId: number) => void;
  handleDeleteClick: (groupId: number) => void;
  handleDuplicateClick: (groupId: number) => void;
  handleEditClick: (groupId: number) => void;
  handleManageClinicSlotsClick: (groupId: number) => void;

  // View Navigation
  handleSearchPatientsClick: () => void;
  handleArchivedGroupsClick: () => void;
  handleManageVPEClick: () => void;
  handleCreateGroupClick: () => void;
  handleBackToDashboard: () => Promise<void>;

  // Patient Operations
  handleSelectPatientFromDropdown: (patient: any) => void;

  // Utility Operations
  handleEmailSuccess: () => void;

  // Data
  groups: Group[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export const useVPEDashboardOperations = (
  state: VPEDashboardState,
  actions: VPEDashboardStateActions
) => {
  const { groups, isLoading, refetch } = useGroups();
  const { user, logout, token, hasuraToken } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const groupNavigation = createGroupOperationsNavigation(navigate);

  // Update service context when auth changes
  useEffect(() => {
    if (user?.id && token && hasuraToken) {
      vpeDashboardService.updateContext({
        userId: user.id,
        token: token,
        hasuraToken: hasuraToken
      });
    }
  }, [user?.id, token, hasuraToken]);

  // Update filtered groups when groups change
  useEffect(() => {
    actions.setFilteredGroups(groups);
  }, [groups]);

  /**
   * Handle refresh data
   */
  const handleRefresh = async () => {
    actions.setIsRefreshing(true);
    try {
      await refetch();
      actions.setFilteredGroups(groups);
      toast({
        title: "Success",
        description: "Data refreshed successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
      });
    } finally {
      actions.setIsRefreshing(false);
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    actions.setShowLogoutDialog(false);
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
  };

  /**
   * Handle search groups
   */
  const handleSearch = () => {
    const filtered = vpeDashboardService.filterGroups(groups, state.searchTerm, state.searchBy);
    actions.setFilteredGroups(filtered);
    actions.setCurrentPage(1);
  };

  /**
   * Handle clear search
   */
  const handleClearSearch = () => {
    actions.resetSearch();
    actions.setFilteredGroups(groups);
  };

  /**
   * Handle patient search
   */
  const handlePatientSearch = async () => {
    if (!state.patientSearchTerm.trim()) {
      actions.setSearchedPatients([]);
      actions.setShowSearchDropdown(false);
      return;
    }

    actions.setIsPatientSearchLoading(true);
    actions.setShowSearchDropdown(true);

    try {
      const patients = await vpeDashboardService.searchPatients(
        state.patientSearchTerm,
        state.patientSearchBy
      );
      actions.setSearchedPatients(patients);
    } catch (error) {
      toast({
        title: "Search Error",
        description: error instanceof Error ? error.message : "Failed to search patients",
        variant: "destructive",
      });
      actions.setSearchedPatients([]);
    } finally {
      actions.setIsPatientSearchLoading(false);
    }
  };

  /**
   * Handle search input change with debounced search
   */
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    actions.setPatientSearchTerm(value);

    if (value.trim()) {
      // Debounce search
      const timeoutId = setTimeout(() => {
        handlePatientSearch();
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      actions.resetPatientSearch();
    }
  };

  /**
   * Handle archive group
   */
  const handleArchiveGroup = async () => {
    if (!state.selectedGroupId) return;

    actions.setIsArchiveLoading(true);
    try {
      await vpeDashboardService.archiveGroup(state.selectedGroupId);

      toast({
        title: "Success",
        description: "Group archived successfully!",
      });

      actions.setShowArchiveDialog(false);
      await refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to archive group",
        variant: "destructive",
      });
    } finally {
      actions.setIsArchiveLoading(false);
    }
  };

  /**
   * Handle delete group
   */
  const handleDeleteGroup = async () => {
    if (!state.selectedGroupId) return;

    actions.setIsDeleteLoading(true);
    try {
      await vpeDashboardService.deleteGroup(state.selectedGroupId);

      toast({
        title: "Success",
        description: "Group deleted successfully!",
      });

      actions.setShowDeleteDialog(false);
      await refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete group",
        variant: "destructive",
      });
    } finally {
      actions.setIsDeleteLoading(false);
    }
  };

  /**
   * Handle add patient to group
   */
  const handleAddPatientToGroup = async () => {
    if (!state.selectedPatient) {
      toast({
        title: "No patient selected",
        description: "Please select a patient to add to the group",
        variant: "destructive",
      });
      return;
    }

    try {
      await vpeDashboardService.addPatientToGroup(state.selectedPatient);

      actions.setPatientCount(state.patientCount + 1);
      actions.setSelectedPatient(null);
      actions.setPatientSearchTerm('');

      toast({
        title: "Success",
        description: "Patient added successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add patient",
        variant: "destructive",
      });
    }
  };

  /**
   * Handle email click
   */
  const handleEmailClick = (groupId: number) => {
    actions.setSelectedGroupId(groupId);
    actions.setShowEmailDialog(true);
  };

  /**
   * Handle archive click
   */
  const handleArchiveClick = (groupId: number) => {
    actions.setArchiveButtonLoading(groupId);
    actions.setSelectedGroupId(groupId);
    actions.setShowArchiveDialog(true);
    // Reset loading after dialog opens
    setTimeout(() => actions.setArchiveButtonLoading(null), 100);
  };

  /**
   * Handle delete click
   */
  const handleDeleteClick = (groupId: number) => {
    actions.setSelectedGroupId(groupId);
    actions.setShowDeleteDialog(true);
  };

  /**
   * Handle duplicate click
   */
  const handleDuplicateClick = (groupId: number) => {
    groupNavigation.navigateToDuplicateGroup(groupId);
  };

  /**
   * Handle edit click
   */
  const handleEditClick = (groupId: number) => {
    groupNavigation.navigateToEditGroup(groupId);
  };

  /**
   * Handle manage clinic slots click
   */
  const handleManageClinicSlotsClick = (groupId: number) => {
    actions.setEditGroupId(groupId);
    actions.setCurrentView('manage-clinic-slots');
  };

  /**
   * Handle search patients click
   */
  const handleSearchPatientsClick = () => {
    navigate('/global-search');
  };

  /**
   * Handle archived groups click
   */
  const handleArchivedGroupsClick = () => {
    navigate('/archived-groups');
  };

  /**
   * Handle manage VPE click
   */
  const handleManageVPEClick = () => {
    navigate('/manage-es');
  };

  /**
   * Handle create group click
   */
  const handleCreateGroupClick = () => {
    groupNavigation.navigateToCreateGroup();
  };

  /**
   * Handle back to dashboard
   */
  const handleBackToDashboard = async () => {
    actions.setCurrentView('dashboard');
    actions.setEditGroupId(null);
    actions.setDuplicateGroupId(null);
    // Refresh data to show updated group list
    await refetch();
  };

  /**
   * Handle select patient from dropdown
   */
  const handleSelectPatientFromDropdown = (patient: any) => {
    actions.setSelectedPatient(patient);
    actions.setPatientSearchTerm(patient.fullString);
    actions.setSearchedPatients([]);
    actions.setShowSearchDropdown(false);
  };

  /**
   * Handle email success
   */
  const handleEmailSuccess = () => {
    refetch();
  };

  return {
    handleRefresh,
    handleLogout,
    handleSearch,
    handleClearSearch,
    handlePatientSearch,
    handleSearchInputChange,
    handleArchiveGroup,
    handleDeleteGroup,
    handleAddPatientToGroup,
    handleEmailClick,
    handleArchiveClick,
    handleDeleteClick,
    handleDuplicateClick,
    handleEditClick,
    handleManageClinicSlotsClick,
    handleSearchPatientsClick,
    handleArchivedGroupsClick,
    handleManageVPEClick,
    handleCreateGroupClick,
    handleBackToDashboard,
    handleSelectPatientFromDropdown,
    handleEmailSuccess,
    groups,
    isLoading,
    refetch,
  };
};